/**
 * Contact provenance helpers (multi-provider contacts redesign §3.5).
 *
 * `transformObject` normalizes a contact's emails/phones into the rich
 * `emailEntries` / `phoneEntries` ({ value, primary?, sources }) — the single
 * source of truth carrying both the value AND which account(s) supplied it. The
 * display renders those entries directly (value + provider icons); these helpers
 * derive the contact-level "draws from" set and group an element's sources for
 * tooltips. Native/legacy contacts have no `sources` → the helpers return empty.
 */
import type { ContactElement, ElementSource, FieldConflict } from '@/lib/api/endpoints/objects.transform';

export type { ElementSource, ContactElement, FieldConflict };

interface WithEntries {
  emailEntries?: ContactElement[];
  phoneEntries?: ContactElement[];
  attributes?: Record<string, unknown> | null;
  fieldProvenance?: Record<string, { conflicted?: boolean; candidates?: Array<{ value?: unknown; sources?: ElementSource[] }> }>;
}

/** A conflicted scalar field with its divergent candidate values + attribution (§3.6). */
export interface ConflictedField {
  field: string;
  candidates: Array<{ value: string; sources: ElementSource[] }>;
}

/**
 * The set of provider-integration ids this contact draws from — the union of
 * every element's sources plus the primary single-field link. Backs the contact
 * header "draws from" badges (§3.5), derived client-side (no per-contact API).
 */
export function contactSourceIds(contact: WithEntries | null | undefined): string[] {
  const ids = new Set<string>();
  for (const entries of [contact?.emailEntries, contact?.phoneEntries]) {
    for (const entry of entries ?? []) {
      for (const s of entry.sources ?? []) {
        if (s?.providerIntegrationId) ids.add(s.providerIntegrationId);
      }
    }
  }
  const primary = contact?.attributes?.providerIntegrationId;
  if (typeof primary === 'string' && primary) ids.add(primary);
  return [...ids];
}

/**
 * The scalar fields whose value diverges across provider accounts (§3.6). Reads
 * the canonical top-level `fieldProvenance` that `transformObject` lifts from
 * `attributes` (the chokepoint delivers it — callers must pass a transformed
 * contact; this reader does NOT re-read `attributes.fieldProvenance`), keeps only
 * entries that are `conflicted` AND carry more than one candidate, and coerces
 * each candidate value to a trimmed display string (the backend types value as
 * `unknown`), dropping empties. Backs the read-only conflict indicator; the
 * write-side resolver ("Use this value") is a deliberate follow-up. Returns []
 * for null/native/legacy contacts (no fieldProvenance → no UI).
 */
export function conflictedFields(contact: WithEntries | null | undefined): ConflictedField[] {
  const fp = contact?.fieldProvenance;
  if (!fp) return [];
  const out: ConflictedField[] = [];
  for (const [field, prov] of Object.entries(fp)) {
    if (!prov?.conflicted) continue;
    const candidates = (prov.candidates ?? [])
      .map((c) => ({ value: String(c?.value ?? '').trim(), sources: c?.sources ?? [] }))
      .filter((c) => c.value.length > 0);
    if (candidates.length > 1) out.push({ field, candidates });
  }
  return out;
}

/**
 * Group an element's sources by account → the distinct signal types that account
 * vouched for it (e.g. Google → ['contacts','email']). Drives the per-icon
 * tooltip ("Google: address book, email").
 */
export function groupSourcesByAccount(sources: ElementSource[] | null | undefined): Map<string, string[]> {
  const byAccount = new Map<string, string[]>();
  for (const s of sources ?? []) {
    if (!s?.providerIntegrationId) continue;
    const signals = byAccount.get(s.providerIntegrationId) ?? [];
    if (s.sourceType && !signals.includes(s.sourceType)) signals.push(s.sourceType);
    byAccount.set(s.providerIntegrationId, signals);
  }
  return byAccount;
}
